// Pared-down focusable-element detection based on the `tabbable` library.
// Intentionally omits low-probability cases for this codebase: radio-group
// deduplication, shadow DOM traversal, positive-tabindex sort ordering,
// audio/video[controls], contenteditable, and details>summary.
// If any of those edge cases become relevant, refer to the full implementation:
// https://github.com/focus-trap/tabbable/blob/master/src/index.js
const FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"]):not([inert]):not([inert] *)',
    'button:not([disabled]):not([tabindex="-1"]):not([inert]):not([inert] *)',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]):not([inert]):not([inert] *)',
    'select:not([disabled]):not([tabindex="-1"]):not([inert]):not([inert] *)',
    'textarea:not([disabled]):not([tabindex="-1"]):not([inert]):not([inert] *)',
    '[tabindex]:not([tabindex="-1"]):not([inert]):not([inert] *)',
].join(', ');
const isVisible = (el) => {
    if (getComputedStyle(el).visibility === 'hidden')
        return false;
    if (el.closest('details:not([open])'))
        return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
};
// Pared-down focus trap based on the `focus-trap` library.
// Intentionally omits: trap stacking/pause/unpause, subtree isolation
// (aria-hidden/inert on adjacent elements), MutationObserver for DOM removal,
// multiple containers, shadow DOM composition, preventScroll, and input.select().
// Escape-key deactivation is also omitted — callers should handle that themselves
// since deactivating the trap alone (without closing the triggering UI) is incomplete.
// If any of those features become relevant, refer to the full implementation:
// https://github.com/focus-trap/focus-trap/blob/master/index.js
export function createFocusTrap(container) {
    let previouslyFocused = null;
    let keydownHandler = null;
    let focusinHandler = null;
    let intervalId = null;
    let initialFocusTimer = null;
    const getFocusable = () => Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(isVisible);
    const trapTab = (e) => {
        if (e.key !== 'Tab')
            return;
        const focusable = getFocusable();
        if (!focusable.length)
            return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };
    // If focus somehow escapes the container (browser chrome, screen reader, programmatic
    // call, etc.), pull it back to the most recently focused node inside the trap.
    const recaptureFocus = (e) => {
        if (!container.contains(e.target)) {
            const focusable = getFocusable();
            if (focusable.length)
                focusable[0].focus();
        }
    };
    return {
        activate() {
            previouslyFocused = document.activeElement;
            keydownHandler = trapTab;
            focusinHandler = recaptureFocus;
            document.addEventListener('keydown', keydownHandler);
            document.addEventListener('focusin', focusinHandler);
            // Delay initial focus by one tick so the element that triggered activation
            // (e.g. a button click) does not immediately receive keyboard events in the trap.
            const tryFocus = () => {
                const focusable = getFocusable();
                if (focusable.length) {
                    clearInterval(intervalId);
                    intervalId = null;
                    focusable[0].focus();
                }
            };
            initialFocusTimer = setTimeout(() => {
                initialFocusTimer = null;
                tryFocus();
                if (!getFocusable().length) {
                    intervalId = setInterval(tryFocus, 300);
                }
            }, 0);
        },
        deactivate() {
            if (initialFocusTimer !== null) {
                clearTimeout(initialFocusTimer);
                initialFocusTimer = null;
            }
            if (keydownHandler) {
                document.removeEventListener('keydown', keydownHandler);
                keydownHandler = null;
            }
            if (focusinHandler) {
                document.removeEventListener('focusin', focusinHandler);
                focusinHandler = null;
            }
            if (intervalId !== null) {
                clearInterval(intervalId);
                intervalId = null;
            }
            previouslyFocused?.focus();
            previouslyFocused = null;
        }
    };
}
